import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, ArrowLeft, Play, ChevronLeft, ChevronRight, Users, BookOpen, Award, Smartphone, GraduationCap, Brain, Shield, MessageCircle, Clock, Target, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/shared/Footer';
import axios from 'axios';

// Check for password recovery token
(function checkForRecoveryToken() {
  const hash = window.location.hash;
  if (hash && window.location.pathname === '/') {
    const hashParams = new URLSearchParams(hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');
    if (accessToken && type === 'recovery') {
      window.location.replace(`/reset-password${hash}`);
    }
  }
})();

export default function Home() {
  const { t, i18n } = useTranslation('home');
  const isRTL = i18n.language === 'ar';
  const [academicCourses, setAcademicCourses] = useState([]);
  const [skillsCourses, setSkillsCourses] = useState([]);
  const [institutions, setInstitutions] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Hero slides data
  const heroSlides = [
    {
      title: t('platform.subtitle'),
      highlight: t('platform.subtitleHighlight'),
      description: t('hero.description'),
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    },
    {
      title: t('hero.title'),
      highlight: t('hero.titleHighlight'),
      description: t('platform.quote'),
      image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    },
    {
      title: t('impact.title'),
      highlight: t('impact.badge'),
      description: t('impact.subtitle'),
      image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    }
  ];

  // Auto-slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, [heroSlides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, [heroSlides.length]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      if (accessToken && type === 'recovery') {
        setRedirecting(true);
        window.location.replace(`/reset-password${hash}`);
      }
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [coursesRes, institutionsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/courses/`).catch(e => ({ data: { courses: [] } })),
          axios.get(`${BACKEND_URL}/api/institutions/`).catch(e => ({ data: { institutions: [] } }))
        ]);
        
        const courses = coursesRes.data?.courses || [];
        const institutionsList = institutionsRes.data?.institutions || [];
        
        const institutionsMap = {};
        institutionsList.forEach(inst => { institutionsMap[inst.id] = inst; });
        setInstitutions(institutionsMap);
        
        const academic = courses.filter(c => c.type === 'academic').sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 4);
        const skills = courses.filter(c => c.type === 'life-skills' || c.type === 'skills').sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 4);
        
        setAcademicCourses(academic);
        setSkillsCourses(skills);
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
      
      try {
        const teamRes = await axios.get(`${BACKEND_URL}/api/team/`);
        const team = teamRes.data?.team || [];
        setTeamMembers(team.slice(0, 7));
      } catch (error) {
        console.error('Error fetching team:', error);
      }
      
      setLoading(false);
    };
    fetchData();
  }, [BACKEND_URL]);

  if (redirecting) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const getLevelBadgeColor = (level) => {
    switch(level?.toLowerCase()) {
      case 'beginner': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'intermediate': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'advanced': return 'bg-purple-50 text-purple-700 border-purple-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  // Animated counter component
  const AnimatedCounter = ({ target, suffix = '' }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      const duration = 2000;
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    }, [target]);
    
    return <span>{count}{suffix}</span>;
  };

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* HERO SLIDER - TETCO STYLE */}
      <section className="relative h-screen overflow-hidden bg-slate-900">
        {/* Header integrated into slider */}
        <header className="absolute top-0 left-0 right-0 z-50 bg-transparent">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              {/* Logo */}
              <Link to="/" className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                  alt="FaheemGPT" 
                  className="w-10 h-10 rounded-xl"
                />
                <span className="text-white text-xl font-bold tracking-tight">FaheemGPT</span>
              </Link>
              
              {/* Navigation */}
              <nav className={`hidden md:flex items-center gap-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Link to="/about" className="text-white/80 hover:text-white transition-colors font-medium">{t('nav.about')}</Link>
                <Link to="/meet-founder" className="text-white/80 hover:text-white transition-colors font-medium">{t('nav.meetFounder', 'Meet Founder')}</Link>
                <Link to="/partners" className="text-white/80 hover:text-white transition-colors font-medium">{t('nav.partners')}</Link>
                <Link to="/how-it-works" className="text-white/80 hover:text-white transition-colors font-medium">{t('nav.howItWorks')}</Link>
                <Link to="/login" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">{t('nav.login', 'Login')}</Link>
              </nav>

              {/* Language Switcher */}
              <button 
                onClick={() => i18n.changeLanguage(isRTL ? 'en' : 'ar')}
                className="text-white/80 hover:text-white transition-colors text-sm font-medium"
              >
                {isRTL ? 'EN' : 'العربية'}
              </button>
            </div>
          </div>
        </header>

        {/* Slides */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className="absolute inset-0"
            style={{
              opacity: index === currentSlide ? 1 : 0,
              visibility: index === currentSlide ? 'visible' : 'hidden',
              transition: 'opacity 800ms ease-in-out, visibility 800ms ease-in-out',
              zIndex: index === currentSlide ? 10 : 0
            }}
          >
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="absolute inset-0 bg-slate-900/70"></div>
            </div>
            
            {/* Content - Centered */}
            <div className="relative h-full flex items-center justify-center">
              <div className="max-w-4xl mx-auto px-6 text-center">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                  {slide.title}
                  <span className="block text-blue-400 mt-2">{slide.highlight}</span>
                </h1>
                <p className="text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl mx-auto">
                  {slide.description}
                </p>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={isRTL ? nextSlide : prevSlide}
          className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-6' : 'left-6'} z-20 w-14 h-14 rounded-full border-2 border-white/30 hover:border-white/60 flex items-center justify-center text-white/60 hover:text-white transition-all`}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={isRTL ? prevSlide : nextSlide}
          className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-6' : 'right-6'} z-20 w-14 h-14 rounded-full border-2 border-white/30 hover:border-white/60 flex items-center justify-center text-white/60 hover:text-white transition-all`}
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-white w-8' : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-8 z-20">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* TAGLINE SECTION */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-800 leading-relaxed">
            {t('tagline.title', 'Between Innovation and Excellence.')} <span className="text-blue-600">{t('tagline.highlight', 'We Innovate!')}</span>
          </h2>
        </div>
      </section>

      {/* TWO COLUMN INTRO SECTION */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className={`grid md:grid-cols-2 gap-12 ${isRTL ? '' : ''}`}>
            {/* Academic Excellence */}
            <div className={`${isRTL ? 'text-right' : ''}`}>
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t('academicCourses.badge', 'Academic Excellence')}</span>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2 mb-4">{t('academicCourses.title')}</h3>
              <p className="text-slate-600 leading-relaxed">
                {t('academicCourses.description')}
              </p>
            </div>

            {/* Life Skills */}
            <div className={`${isRTL ? 'text-right' : ''}`}>
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t('skillsCourses.badge', 'Life Skills')}</span>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2 mb-4">{t('skillsCourses.title')}</h3>
              <p className="text-slate-600 leading-relaxed">
                {t('skillsCourses.introDescription', 'We go beyond academics to help students build essential life and career skills. Through real-time conversations with FaheemGPT, students learn how to write effective CVs, prepare for job interviews, and present their ideas confidently — developing the confidence and readiness to excel beyond the classroom.')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE FAHEEMGPT - WITH VIDEO BACKGROUND */}
      <section className="relative py-20 px-6 overflow-hidden">
        {/* Video Background with overlay */}
        <div className="absolute inset-0">
          <video 
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay 
            loop 
            muted 
            playsInline
          >
            <source src="https://customer-assets.emergentagent.com/job_f8ffea51-d543-4d89-8a26-4f74f0b6ea0e/artifacts/0rcd41ep_video.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-slate-900/85"></div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className={`grid lg:grid-cols-2 gap-16 items-center ${isRTL ? '' : ''}`}>
            {/* Left Side - Content */}
            <div className={`${isRTL ? 'lg:order-last text-right' : ''}`}>
              {/* Title */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                {t('platform.badge')}
              </h2>
              
              {/* Description */}
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                {t('platform.quote')}
              </p>
              
              {/* Bullet Points - 2 Columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {[
                  t('features.noHallucination'),
                  t('features.humanLikeInteraction'),
                  t('features.safeForAllAges'),
                  t('features.intelligentAssistant'),
                  t('features.fullyCustomizable'),
                  t('impact.collaboration'),
                  t('impact.teacherSupport'),
                  t('impact.skillsEnhancement'),
                ].map((item, idx) => (
                  <div key={idx} className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Video */}
            <div className={`relative ${isRTL ? 'lg:order-first' : ''}`}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10">
                <video 
                  className="w-full h-auto"
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                >
                  <source src="https://customer-assets.emergentagent.com/job_ai-education-10/artifacts/bf4mqye2_Recording%202025-12-04%20105049.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION - WITH WHITE BACKGROUND */}
      <section className="relative py-20 px-6">
        {/* Background with white overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ai-education-10/artifacts/7eg7g8ge_graybackground.jpg)' }}
        >
          <div className="absolute inset-0 bg-white/90"></div>
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">{t('cta.title')}</h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">{t('cta.description')}</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/login">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-6 rounded-full text-lg font-semibold">
                {t('cta.getStarted')} <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
            <Link to="/partners">
              <Button variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-8 py-6 rounded-full text-lg font-semibold">
                <Users className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('cta.becomePartner')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* TEAM SECTION - WITH BACKGROUND IMAGE */}
      {teamMembers.length > 0 && (
        <section className="relative py-20 px-6">
          {/* Background with overlay */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-fixed"
            style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)' }}
          >
            <div className="absolute inset-0 bg-slate-900/90"></div>
          </div>

          <div className="relative max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">{t('team.badge', 'Our Team')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">{t('team.title')}</h2>
              <p className="text-white/70 text-lg max-w-2xl mx-auto">{t('team.description')}</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6">
              {teamMembers.slice(0, 7).map((member, idx) => (
                <div key={member.id || idx} className="group text-center">
                  <div className="relative mb-4 overflow-hidden rounded-2xl border-2 border-white/10">
                    <img 
                      src={member.image_url || member.photo_url || 'https://via.placeholder.com/200'} 
                      alt={member.name}
                      className="w-full aspect-square object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/200?text=Team'; }}
                    />
                    <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/30 transition-colors duration-300"></div>
                  </div>
                  <h3 className="font-bold text-white group-hover:text-blue-400 transition-colors text-sm">{member.name}</h3>
                  <p className="text-xs text-white/60 mt-1">{member.title || member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PARTNERS SECTION - WITH WHITE BACKGROUND */}
      {Object.keys(institutions).length > 0 && (
        <section className="relative py-20 px-6">
          {/* Background with white overlay */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-fixed"
            style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ai-education-10/artifacts/7eg7g8ge_graybackground.jpg)' }}
          >
            <div className="absolute inset-0 bg-white/90"></div>
          </div>

          <div className="relative max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t('partners.badge', 'Our Partners')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">{t('partners.title', 'Trusted By Leading Institutions')}</h2>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">{t('partners.description', 'We collaborate with top universities and educational organizations to deliver excellence in education.')}</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {Object.values(institutions).map((institution, idx) => (
                <div key={institution.id || idx} className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 mb-4 rounded-xl bg-slate-50 p-3 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <img 
                        src={institution.logo_url} 
                        alt={institution.name}
                        className="w-full h-full object-contain"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/80?text=Logo'; }}
                      />
                    </div>
                    <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors text-sm">{institution.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOOTER - Using shared component */}
      <Footer />
    </div>
  );
}
